import React, { useEffect } from 'react';
import { useQuickBooks } from '../../hooks/useQuickBooks';
import styles from './QuickBooksIntegration.module.css';

/**
 * Format time remaining until expiry
 */
const formatTimeRemaining = (expiresAt) => {
  if (!expiresAt) return 'N/A';
  
  const now = new Date();
  const expiry = new Date(expiresAt);
  const diffMs = expiry.getTime() - now.getTime();
  
  if (diffMs <= 0) return 'Expired';
  
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

/**
 * Get status color class based on time remaining
 */
const getStatusColorClass = (expiresAt, isRefreshToken) => {
  if (!expiresAt) return 'normal';
  
  const now = new Date();
  const expiry = new Date(expiresAt);
  const diffMs = expiry.getTime() - now.getTime();
  const diffDays = diffMs / (1000 * 60 * 60 * 24);
  
  if (isRefreshToken) {
    // Refresh token warnings
    if (diffDays < 10) return 'critical';    // Critical: < 10 days
    if (diffDays < 30) return 'warning';     // Warning: < 30 days
    return 'success';
  } else {
    // Access token warnings
    const diffMinutes = diffMs / (1000 * 60);
    if (diffMinutes < 10) return 'warning';  // Warning: < 10 minutes
    return 'success';
  }
};

const QuickBooksIntegration = () => {
  const { 
    status, 
    loading, 
    error, 
    isConnected,
    connect, 
    disconnect, 
    refetch 
  } = useQuickBooks();

  // Poll status every 30 seconds to keep timers updated
  useEffect(() => {
    if (!isConnected) return;
    
    const interval = setInterval(() => {
      refetch();
    }, 30000); // 30 seconds
    
    return () => clearInterval(interval);
  }, [isConnected, refetch]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  const handleConnect = async () => {
    await connect();
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect from QuickBooks?')) {
      return;
    }
    await disconnect();
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2>QuickBooks Integration</h2>
        <p className={styles.description}>
          Connect your QuickBooks account to enable automatic invoicing and financial integrations.
        </p>
      </div>

      {loading && !status && (
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <p>Loading connection status...</p>
        </div>
      )}

      {error && (
        <div className={styles.errorBox}>
          <span className={styles.errorIcon}>⚠️</span>
          <div>
            <strong>Error:</strong> {error}
          </div>
        </div>
      )}

      {status && !loading && (
        <div className={styles.statusCard}>
          {isConnected ? (
            <>
              <div className={styles.statusHeader}>
                <div className={styles.statusBadge}>
                  <span className={styles.statusDot} data-status="connected"></span>
                  <span className={styles.statusText}>Connected</span>
                </div>
                <button 
                  onClick={refetch} 
                  disabled={loading}
                  className={styles.refreshButton}
                  title="Refresh status"
                >
                  🔄
                </button>
              </div>

              <div className={styles.details}>
                <div className={styles.detailRow}>
                  <span className={styles.label}>Company ID:</span>
                  <span className={styles.value}>{status.realmId || 'N/A'}</span>
                </div>

                {/* Access Token Status */}
                {status.accessTokenExpiresAt && (
                  <div className={styles.detailRow}>
                    <span className={styles.label}>Token Expires:</span>
                    <span className={styles.value}>
                      {formatDate(status.accessTokenExpiresAt)}
                      <span className={`${styles.timeRemaining} ${styles[getStatusColorClass(status.accessTokenExpiresAt, false)]}`}>
                        {' '}({formatTimeRemaining(status.accessTokenExpiresAt)})
                      </span>
                    </span>
                  </div>
                )}

                {status.accessTokenExpired && (
                  <div className={styles.infoAlert}>
                    ℹ️ Access token expired - Backend will auto-refresh on next API call
                  </div>
                )}

                {/* Refresh Token Status */}
                {status.refreshTokenExpiresAt && (
                  <>
                    <div className={styles.detailRow}>
                      <span className={styles.label}>Re-auth Required:</span>
                      <span className={styles.value}>
                        {formatDate(status.refreshTokenExpiresAt)}
                        <span className={`${styles.timeRemaining} ${styles[getStatusColorClass(status.refreshTokenExpiresAt, true)]}`}>
                          {' '}({formatTimeRemaining(status.refreshTokenExpiresAt)})
                        </span>
                      </span>
                    </div>

                    {/* Warning for refresh token expiry */}
                    {(() => {
                      const now = new Date();
                      const expiry = new Date(status.refreshTokenExpiresAt);
                      const daysRemaining = (expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
                      
                      if (status.refreshTokenExpired) {
                        return (
                          <div className={styles.criticalAlert}>
                            ❌ Refresh token expired! Please reconnect to QuickBooks.
                          </div>
                        );
                      } else if (daysRemaining < 10) {
                        return (
                          <div className={styles.criticalAlert}>
                            ⚠️ Critical: Re-authorization needed in {Math.ceil(daysRemaining)} days!
                          </div>
                        );
                      } else if (daysRemaining < 30) {
                        return (
                          <div className={styles.warningAlert}>
                            ⚠️ Re-authorization recommended within {Math.ceil(daysRemaining)} days
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </>
                )}
              </div>

              {/* Status explanation */}
              <div className={styles.infoBox}>
                <h4>📌 How Token Refresh Works</h4>
                <ul>
                  <li><strong>Access Token:</strong> Refreshes automatically every 50 minutes (1 hour lifespan)</li>
                  <li><strong>Re-authorization:</strong> Required every 100 days (backend will notify you)</li>
                  <li><strong>Status Polling:</strong> Updates every 30 seconds</li>
                </ul>
              </div>

              <button 
                onClick={handleDisconnect}
                disabled={loading}
                className={styles.disconnectButton}
              >
                {loading ? 'Disconnecting...' : 'Disconnect'}
              </button>
            </>
          ) : (
            <>
              <div className={styles.statusHeader}>
                <div className={styles.statusBadge}>
                  <span className={styles.statusDot} data-status="disconnected"></span>
                  <span className={styles.statusText}>Not Connected</span>
                </div>
                <button 
                  onClick={refetch} 
                  disabled={loading}
                  className={styles.refreshButton}
                  title="Refresh status"
                >
                  🔄
                </button>
              </div>

              <p className={styles.info}>
                Connect your QuickBooks account to automatically create invoices when onboarding new clients.
              </p>

              <button 
                onClick={handleConnect}
                className={styles.connectButton}
              >
                Connect to QuickBooks
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default QuickBooksIntegration;
